import { useEffect } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useGetMyProfile,
  useUpsertMyProfile,
  getGetMyProfileQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Input } from "@workspace/ui-web/components/ui/input";
import { Textarea } from "@workspace/ui-web/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui-web/components/ui/form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import { Link } from "wouter";

const profileSchema = z.object({
  dogName: z.string().min(1, "Dog name is required"),
  dogBreed: z.string().optional(),
  phone: z.string().optional(),
  location: z.string().optional(),
  bio: z.string().optional(),
  startedAt: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export default function PortalProfile() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useGetMyProfile();
  const upsertProfile = useUpsertMyProfile();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      dogName: "",
      dogBreed: "",
      phone: "",
      location: "",
      bio: "",
      startedAt: "",
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        dogName: profile.dogName || "",
        dogBreed: profile.dogBreed || "",
        phone: profile.phone || "",
        location: profile.location || "",
        bio: profile.bio || "",
        startedAt: profile.startedAt ? profile.startedAt.split("T")[0] : "",
      });
    }
  }, [profile, form]);

  const onSubmit = (data: ProfileFormValues) => {
    upsertProfile.mutate(
      { data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetMyProfileQueryKey(),
          });
          toast({
            title: "Profile saved",
            description: "Your profile has been updated successfully.",
          });
          setLocation("/portal");
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to save profile.",
            variant: "destructive",
          });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 py-8 mx-auto max-w-2xl">
        <div className="mb-6">
          <Link
            href="/portal"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Portal
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Raiser Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="dogName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dog's Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Buddy" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dogBreed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dog's Breed</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Golden Retriever"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 555-0123" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City, State</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Austin, TX" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="startedAt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>About You & Your Dog</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Share a bit about your environment and schedule..."
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-4 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation("/portal")}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={upsertProfile.isPending}>
                    {upsertProfile.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Profile
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
